import React, { useEffect, useState } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import CustomTooltip from '../Utils/CustomTooltip';
import Editor from '@monaco-editor/react';
import DuplicateRoleItem from './DuplicateRoleItem';
import EditRoleItem from './EditRoleItem';
import FullScreenEditor from './FullScreenEditor';

const ActionPrompts = () => {
  const { user } = useSession();
  const [selectedRole, setSelectedRole] = useState(0); // Index of selected role item
  const [selectedAction, setSelectedAction] = useState('CREATE QUESTIONNAIRE');
  const [selectedType, setSelectedType] = useState('JOB');
  const [roleItems, setRoleItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionsError, setActionsError] = useState(null);
  const [promptContent, setPromptContent] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState(null);
  const [promptChanges, setPromptChanges] = useState({}); // Track changes for each prompt
  const [schemaChanges, setSchemaChanges] = useState({}); // Track changes for each schema
  const [savingPrompts, setSavingPrompts] = useState({}); // Track saving state for each prompt
  const [openMenus, setOpenMenus] = useState({}); // Track which role menus are open
  const [duplicateModal, setDuplicateModal] = useState({ isOpen: false, roleData: null, roleIndex: null, promptId: null });
  const [editModal, setEditModal] = useState({ isOpen: false, roleData: null, roleIndex: null });
  const [focusedEditor, setFocusedEditor] = useState({}); // Track which editor is focused for each prompt
  const [fullScreenEditor, setFullScreenEditor] = useState({ isOpen: false, promptData: null }); // Track full-screen editor state

  // URL parameter management
  const updateURL = (role, action, type) => {
    const url = new URL(window.location);
    url.searchParams.set('role', role.toString());
    url.searchParams.set('action', action);
    url.searchParams.set('type', type);
    window.history.replaceState({}, '', url);
  };

  const loadFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const roleParam = urlParams.get('role');
    const actionParam = urlParams.get('action');
    const typeParam = urlParams.get('type');
    
    return {
      role: roleParam ? parseInt(roleParam, 10) : null,
      action: actionParam || null,
      type: typeParam || null
    };
  };

  // Initialize from URL parameters on component mount
  useEffect(() => {
    const urlParams = loadFromURL();
    
    if (urlParams.role !== null && !isNaN(urlParams.role)) {
      setSelectedRole(urlParams.role);
    }
    if (urlParams.action) {
      setSelectedAction(urlParams.action);
    }
    if (urlParams.type) {
      setSelectedType(urlParams.type);
    }
  }, []);

  // Update URL when filters change
  useEffect(() => {
    if (roleItems.length > 0 && actions.length > 0) {
      updateURL(selectedRole, selectedAction, selectedType);
    }
  }, [selectedRole, selectedAction, selectedType, roleItems, actions]);

  // Fetch role items from server
  useEffect(() => {
    const fetchRoleItems = async () => {
      if (!user?.access_token) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(API_ENDPOINTS.GET_UNIQUE_CAT_PAIRS, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Role items loaded:', data);
        
        setRoleItems(data);
        
        // Validate and set role from URL parameters after data is loaded
        const urlParams = loadFromURL();
        if (urlParams.role !== null && !isNaN(urlParams.role) && urlParams.role >= 0 && urlParams.role < data.length) {
          setSelectedRole(urlParams.role);
        }
        
      } catch (err) {
        console.error('Error loading role items:', err);
        setError('Failed to load role items. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRoleItems();
  }, [user]);

  // Fetch actions from server
  useEffect(() => {
    const fetchActions = async () => {
      if (!user?.access_token) return;
      
      try {
        setActionsLoading(true);
        setActionsError(null);
        
        const response = await fetch(API_ENDPOINTS.GET_UNIQUE_ACTIONS, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Actions loaded:', data);
        
        // Extract action strings from the response objects
        const actionStrings = data.map(item => item.action);
        setActions(actionStrings);
        
        // Check if we have a URL parameter for action, otherwise use first action
        const urlParams = loadFromURL();
        if (urlParams.action && actionStrings.includes(urlParams.action)) {
          setSelectedAction(urlParams.action);
        } else if (actionStrings.length > 0) {
          setSelectedAction(actionStrings[0]);
        }
        
      } catch (err) {
        console.error('Error loading actions:', err);
        setActionsError('Failed to load actions. Please try again later.');
      } finally {
        setActionsLoading(false);
      }
    };

    fetchActions();
  }, [user]);

  // Fetch prompt content based on selected filters
  const fetchPromptContent = async () => {
    if (!user?.access_token || !roleItems[selectedRole] || !selectedAction) return;
    
    try {
      setPromptLoading(true);
      setPromptError(null);
      
      // Map frontend types to server types
      const typeMapping = {
        'JOB': 'VSJob',
        'TALENT': 'VSResume', 
        'SKILLSET': 'VSSkillset',
        'NONE': 'General'
      };
      
      const requestBody = {
        role: roleItems[selectedRole],
        action: selectedAction,
        type: typeMapping[selectedType]
      };
      
      console.log('Fetching prompt with filters:', requestBody);
      
      const response = await fetch(API_ENDPOINTS.GET_PROMPT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Prompt content loaded:', data);
      
      setPromptContent(data);
      
    } catch (err) {
      console.error('Error loading prompt content:', err);
      setPromptError('Failed to load prompt content. Please try again later.');
    } finally {
      setPromptLoading(false);
    }
  };

  // Fetch prompt content when filters change
  useEffect(() => {
    if (roleItems.length > 0 && actions.length > 0 && selectedAction) {
      fetchPromptContent();
    }
  }, [selectedRole, selectedAction, selectedType, roleItems, actions]);

  // Reset changes when prompt content changes
  useEffect(() => {
    setPromptChanges({});
    setSchemaChanges({});
  }, [promptContent]);

  // Handle prompt text change
  const handlePromptChange = (promptId, newValue, originalValue) => {
    setPromptChanges(prev => ({
      ...prev,
      [promptId]: {
        hasChanges: newValue !== originalValue,
        newValue: newValue,
        originalValue: originalValue
      }
    }));
  };

  // Handle schema text change
  const handleSchemaChange = (promptId, newValue, originalValue) => {
    setSchemaChanges(prev => ({
      ...prev,
      [promptId]: {
        hasChanges: newValue !== originalValue,
        newValue: newValue,
        originalValue: originalValue
      }
    }));
  };

  // Save prompt function
  const savePrompt = async (promptId, promptText, schemaText) => {
    if (!user?.access_token) return;
    
    try {
      setSavingPrompts(prev => ({ ...prev, [promptId]: true }));
      
      // Parse schema text back to JSON object for database storage
      let parsedSchema;
      try {
        parsedSchema = schemaText ? JSON.parse(schemaText) : null;
      } catch (parseError) {
        console.error('Error parsing schema JSON:', parseError);
        alert('Invalid JSON in schema. Please check the syntax.');
        setSavingPrompts(prev => ({ ...prev, [promptId]: false }));
        return;
      }
      
      const requestBody = {
        id: promptId,
        prompt: promptText,
        output_json_schema: parsedSchema
      };
      
      console.log('Saving prompt and schema:', requestBody);
      
      const response = await fetch(API_ENDPOINTS.SAVE_PROMPTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Prompt and schema saved successfully:', data);
      
      // Clear the changes for this prompt and schema
      setPromptChanges(prev => {
        const updated = { ...prev };
        delete updated[promptId];
        return updated;
      });
      
      setSchemaChanges(prev => {
        const updated = { ...prev };
        delete updated[promptId];
        return updated;
      });
      
      // Update the original prompt content
      setPromptContent(prev => {
        if (Array.isArray(prev)) {
          return prev.map(prompt => 
            prompt.prompt_id === promptId 
              ? { ...prompt, prompt: promptText, output_json_schema: parsedSchema }
              : prompt
          );
        }
        return prev;
      });
      
    } catch (err) {
      console.error('Error saving prompt:', err);
      alert('Failed to save prompt. Please try again.');
    } finally {
      setSavingPrompts(prev => ({ ...prev, [promptId]: false }));
    }
  };

  // Toggle menu for role items
  const toggleMenu = (index) => {
    setOpenMenus(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Close all menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenus({});
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Handle duplicate role
  const handleDuplicateRole = (index) => {
    setDuplicateModal({
      isOpen: true,
      roleData: roleItems[index],
      roleIndex: index
    });
    setOpenMenus({});
  };

  // Handle duplicate modal close
  const handleDuplicateModalClose = () => {
    setDuplicateModal({ isOpen: false, roleData: null, roleIndex: null });
  };

  // Handle duplicate confirmation
  const handleDuplicateConfirm = (duplicatedRole) => {
    setRoleItems(prev => [...prev, duplicatedRole]);
    console.log('Role duplicated:', duplicatedRole);
  };

  // Handle edit role
  const handleEditRole = (index) => {
    setEditModal({
      isOpen: true,
      roleData: roleItems[index],
      roleIndex: index
    });
    setOpenMenus({});
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setEditModal({ isOpen: false, roleData: null, roleIndex: null });
  };

  // Handle edit confirmation
  const handleEditConfirm = (updatedRole) => {
    setRoleItems(prev => prev.map((role, index) => 
      index === editModal.roleIndex ? updatedRole : role
    ));
    console.log('Role updated:', updatedRole);
  };

  // Handle delete role
  const handleDeleteRole = (index) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      setRoleItems(prev => prev.filter((_, i) => i !== index));
      
      // Adjust selected role if necessary
      if (selectedRole >= index) {
        setSelectedRole(Math.max(0, selectedRole - 1));
      }
      
      setOpenMenus({});
      console.log('Deleted role at index:', index);
    }
  };

  // Handle full-screen editor close
  const handleFullScreenClose = () => {
    setFullScreenEditor({ isOpen: false, promptData: null });
  };

  // Handle full-screen editor save
  const handleFullScreenSave = (promptText, schemaText) => {
    if (fullScreenEditor.promptData) {
      const promptId = fullScreenEditor.promptData.prompt_id;
      savePrompt(promptId, promptText, schemaText);
    }
  };

  return (
    <div className="flex h-full">
      {/* First Column - Roles */}
      <div className="w-[300px] flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center">
            <span className="mr-2">→</span>
            ROLES
          </h3>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          {loading && (
            <div className="text-center text-gray-500 py-8">
              <p>Loading role items...</p>
            </div>
          )}
          
          {error && (
            <div className="text-center text-red-500 py-8">
              <p className="mb-2">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
              >
                Retry
              </button>
            </div>
          )}
          
          {!loading && !error && (
            <div className="space-y-3">
              {roleItems.map((item, index) => (
                <div key={index} className="relative">
                  <button
                    onClick={() => setSelectedRole(index)}
                    className={`w-full text-left p-3 border rounded text-xs relative ${
                      selectedRole === index
                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {/* Three-dot menu button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu(index);
                      }}
                      className="absolute top-2 right-2 p-1 rounded hover:bg-gray-200 transition-colors"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>

                    <div className="mb-2 pr-8">
                      <div className="font-semibold text-gray-800 mb-1">Category</div>
                      <div className="text-gray-600">
                        {item.mainCategory.map((cat, i) => (
                          <div key={i} className="text-xs py-0.5">
                            • {cat}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="pr-8">
                      <div className="font-semibold text-gray-800 mb-1">Subcategory</div>
                      <div className="text-gray-600">
                        {item.subCategory.map((sub, i) => (
                          <div key={i} className="text-xs py-0.5">
                            • {sub}
                          </div>
                        ))}
                      </div>
                    </div>
                  </button>

                  {/* Dropdown menu */}
                  {openMenus[index] && (
                    <div className="absolute top-8 right-2 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[120px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditRole(index);
                        }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateRole(index);
                        }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100"
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRole(index);
                        }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Second Column - Actions */}
      <div className="w-[260px] flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center">
            <span className="mr-2">→</span>
            ACTIONS
          </h3>
        </div>
        <div className="p-4 flex-1 flex flex-col overflow-hidden">
          {/* Action Buttons */}
          <div className="space-y-2 flex-1 overflow-y-auto mb-4 min-h-0">
            {actionsLoading && (
              <div className="text-center text-gray-500 py-4">
                <p className="text-sm">Loading actions...</p>
              </div>
            )}
            
            {actionsError && (
              <div className="text-center text-red-500 py-4">
                <p className="mb-2 text-sm">{actionsError}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                >
                  Retry
                </button>
              </div>
            )}
            
            {!actionsLoading && !actionsError && actions.map((action) => (
              <button
                key={action}
                onClick={() => setSelectedAction(action)}
                className={`w-full text-left p-3 border rounded text-sm ${
                  selectedAction === action
                    ? 'border-blue-300 bg-blue-100 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {action}
              </button>
            ))}
          </div>
          
          {/* Context Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 flex items-center mb-3">
              <span className="mr-2">→</span>
              CONTEXT
            </h3>
            <div className="space-y-2">
              <button 
                onClick={() => setSelectedType('JOB')}
                className={`w-full text-left p-3 border rounded text-sm ${
                  selectedType === 'JOB'
                    ? 'border-blue-300 bg-blue-100 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                JOB
              </button>
              <button 
                onClick={() => setSelectedType('TALENT')}
                className={`w-full text-left p-3 border rounded text-sm ${
                  selectedType === 'TALENT'
                    ? 'border-blue-300 bg-blue-100 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                TALENT
              </button>
              <button 
                onClick={() => setSelectedType('SKILLSET')}
                className={`w-full text-left p-3 border rounded text-sm ${
                  selectedType === 'SKILLSET'
                    ? 'border-blue-300 bg-blue-100 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                SKILLSET
              </button>
              <button 
                onClick={() => setSelectedType('NONE')}
                className={`w-full text-left p-3 border rounded text-sm ${
                  selectedType === 'NONE'
                    ? 'border-blue-300 bg-blue-100 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                NONE
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Third Column - Content Area (remaining width) */}
      <div className="flex-1 bg-white overflow-hidden">
        <div className="h-full overflow-auto p-6">
            {promptLoading && (
              <div className="text-center text-gray-500 mt-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-lg">Loading prompt content...</p>
              </div>
            )}
            
            {promptError && (
              <div className="text-center text-red-500 mt-20">
                <p className="text-lg mb-4">{promptError}</p>
                <button 
                  onClick={fetchPromptContent}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Retry
                </button>
              </div>
            )}
            
            {!promptLoading && !promptError && promptContent && promptContent.length > 0 && promptContent[0].prompt_id && (
              <div className="flex gap-4 h-full">
                {promptContent.map((prompt, index) => {
                  const promptId = prompt.prompt_id || index;
                  const hasPromptChanges = promptChanges[promptId]?.hasChanges || false;
                  const hasSchemaChanges = schemaChanges[promptId]?.hasChanges || false;
                  const hasChanges = hasPromptChanges || hasSchemaChanges;
                  const currentPromptValue = promptChanges[promptId]?.newValue || prompt.prompt || '';
                  
                  // Convert schema to string (schema is always a JSON object)
                  const schemaValue = JSON.stringify(prompt.output_json_schema || {}, null, 2);
                  
                  const currentSchemaValue = schemaChanges[promptId]?.newValue || schemaValue;
                  const isSaving = savingPrompts[promptId] || false;
                  
                  return (
                    <div key={promptId} className="flex-shrink-0 min-w-[860px] bg-white rounded-lg border flex flex-col h-full" style={{ overflow: 'visible' }}>
                      {/* Column Header */}
                      <div className="p-4 border-b bg-blue-50 rounded-t-lg flex-shrink-0 flex justify-between items-center" style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}>
                        <div>
                          <CustomTooltip content={prompt.description || 'No description available'} position="bottom">
                            <h4 className="font-semibold text-gray-800 text-sm cursor-help">
                              {prompt.promptNameID} (s:{prompt.action_sequence}, id:{prompt.prompt_id}) 
                            </h4>
                          </CustomTooltip>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {/* Unsaved changes indicator */}
                          {hasChanges && (
                            <span className="text-orange-600 text-xs">Unsaved changes</span>
                          )}
                          
                          {/* Save Button */}
                          <button
                            onClick={() => savePrompt(promptId, currentPromptValue, currentSchemaValue)}
                            disabled={!hasChanges || isSaving}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                              hasChanges && !isSaving
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                          
                          {/* Duplicate Button */}
                          <button
                            onClick={() => setDuplicateModal({
                              isOpen: true,
                              roleData: {
                                mainCategory: roleItems[selectedRole]?.mainCategory || [],
                                subCategory: roleItems[selectedRole]?.subCategory || []
                              },
                              roleIndex: selectedRole,
                              promptId: promptId
                            })}
                            className="px-3 py-1 text-xs font-medium rounded transition-colors bg-green-500 text-white hover:bg-green-600"
                            title="Duplicate this prompt"
                          >
                            Duplicate
                          </button>
                          
                          {/* Full Screen Button */}
                          <button
                            onClick={() => setFullScreenEditor({ 
                              isOpen: true, 
                              promptData: prompt
                            })}
                            className="px-3 py-1 text-xs font-medium rounded transition-colors bg-gray-500 text-white hover:bg-gray-600"
                            title="Open in full screen"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {/* Column Content */}
                      <div className="flex-1 flex flex-col min-h-0 gap-2 p-2">
                        {/* Prompt Editor */}
                        <div className={`flex flex-col min-h-0 ${
                          focusedEditor[promptId] === 'prompt' ? 'flex-[9]' : 
                          focusedEditor[promptId] === 'schema' ? 'flex-1' : 'flex-[9]'
                        }`}>
                          <div className="mb-2">
                            <h5 className="text-xs font-medium text-gray-700 mb-1">PROMPT</h5>
                          </div>
                          <div className="flex-1 border rounded overflow-hidden min-h-0">
                            <Editor
                              key={`prompt-${promptId}`}
                              height="100%"
                              defaultLanguage="markdown"
                              value={currentPromptValue}
                              onChange={(value) => handlePromptChange(promptId, value || '', prompt.prompt || '')}
                              onMount={(editor) => {
                                editor.onDidFocusEditorText(() => {
                                  setFocusedEditor(prev => ({ ...prev, [promptId]: 'prompt' }));
                                });
                              }}
                              options={{
                                readOnly: false,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                wordWrap: 'on',
                                fontSize: 14,
                                lineNumbers: 'off',
                                folding: false,
                                glyphMargin: false,
                                lineDecorationsWidth: 0,
                                lineNumbersMinChars: 0,
                                renderLineHighlight: 'none',
                                automaticLayout: true,
                                'breadcrumbs.enabled': false
                              }}
                              theme="vs"
                            />
                          </div>
                        </div>
                        
                        {/* Schema Editor */}
                        <div className={`flex flex-col min-h-0 ${
                          focusedEditor[promptId] === 'schema' ? 'flex-[9]' : 
                          focusedEditor[promptId] === 'prompt' ? 'flex-1' : 'flex-1'
                        }`}>
                          <div className="mb-2">
                            <h5 className="text-xs font-medium text-gray-700 mb-1">OUTPUT JSON SCHEMA</h5>
                          </div>
                        <div className="flex-1 border rounded overflow-hidden min-h-0">
                          <Editor
                            key={`schema-${promptId}`}
                            height="100%"
                            defaultLanguage="json"
                            value={currentSchemaValue}
                            onChange={(value) => handleSchemaChange(promptId, value || '', schemaValue)}
                            onMount={(editor) => {
                              editor.onDidFocusEditorText(() => {
                                setFocusedEditor(prev => ({ ...prev, [promptId]: 'schema' }));
                              });
                            }}
                            options={{
                              readOnly: false,
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                              wordWrap: 'on',
                              fontSize: 14,
                              lineNumbers: 'on',
                              folding: false,
                              glyphMargin: false,
                              renderLineHighlight: 'none',
                              automaticLayout: true,
                              tabSize: 2,
                              insertSpaces: true,
                              'breadcrumbs.enabled': false
                            }}
                            theme="vs"
                          />
                        </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {!promptLoading && !promptError && promptContent && (promptContent.length === 0 || !promptContent[0].prompt_id) && roleItems.length > 0 && actions.length > 0 && (
              <div className="text-center text-gray-500 mt-20">
                <p className="text-lg">No prompt found for the selected filters</p>
                <p className="text-sm mt-2">Try selecting different role, action, or type combinations</p>
              </div>
            )}
            
            {!promptLoading && !promptError && !promptContent && (roleItems.length === 0 || actions.length === 0) && (
              <div className="text-center text-gray-500 mt-20">
                <p className="text-lg">Waiting for data to load...</p>
                <p className="text-sm mt-2">Please wait while roles and actions are being loaded</p>
              </div>
            )}
        </div>
      </div>

      {/* Duplicate Role Modal */}
      <DuplicateRoleItem
        isOpen={duplicateModal.isOpen}
        onClose={handleDuplicateModalClose}
        roleData={duplicateModal.roleData}
        onConfirm={handleDuplicateConfirm}
        prompt_id={duplicateModal.promptId}
      />

      {/* Edit Role Modal */}
      <EditRoleItem
        isOpen={editModal.isOpen}
        onClose={handleEditModalClose}
        roleData={editModal.roleData}
        onConfirm={handleEditConfirm}
      />

      {/* Full Screen Editor */}
      {fullScreenEditor.isOpen && fullScreenEditor.promptData && (
        <FullScreenEditor
          isOpen={fullScreenEditor.isOpen}
          onClose={handleFullScreenClose}
          promptData={fullScreenEditor.promptData}
          onSave={handleFullScreenSave}
          isSaving={savingPrompts[fullScreenEditor.promptData.prompt_id] || false}
          getCurrentPromptValue={() => {
            const promptId = fullScreenEditor.promptData.prompt_id;
            return promptChanges[promptId]?.newValue || fullScreenEditor.promptData.prompt || '';
          }}
          getCurrentSchemaValue={() => {
            const promptId = fullScreenEditor.promptData.prompt_id;
            return schemaChanges[promptId]?.newValue || JSON.stringify(fullScreenEditor.promptData.output_json_schema || {}, null, 2);
          }}
          onPromptChange={(value) => {
            const promptId = fullScreenEditor.promptData.prompt_id;
            handlePromptChange(promptId, value, fullScreenEditor.promptData.prompt || '');
          }}
          onSchemaChange={(value) => {
            const promptId = fullScreenEditor.promptData.prompt_id;
            const originalSchemaValue = JSON.stringify(fullScreenEditor.promptData.output_json_schema || {}, null, 2);
            handleSchemaChange(promptId, value, originalSchemaValue);
          }}
          hasChanges={(promptChanges[fullScreenEditor.promptData.prompt_id]?.hasChanges || false) || (schemaChanges[fullScreenEditor.promptData.prompt_id]?.hasChanges || false)}
        />
      )}
    </div>
  );
};

export default ActionPrompts;
